import { Alert, Button, Card, Input, Space } from "antd";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    MultiGrid as ZhMultiGrid
} from "zhihao-ui-react";

import request from "@/api";
import { useUserStore } from "@/views/stores";
import ProductDetailMapDialog from "./ProductDetailMapDialog";
import type { GroupPurchaseRow } from "./types";

export interface GroupPurchaseDetailPageProps {
    auditGroupPurchase?: (id: number | string, payload: { state: boolean; auditDescn: string }) => Promise<void>;
    closeGroupPurchase?: (id: number | string, unActiveFileIds?: number[]) => Promise<void>;
    downShelfGroupPurchase?: (id: number | string) => Promise<void>;
    loadGroupPurchase?: (id: number | string) => Promise<GroupPurchaseRow>;
    permissions?: string[];
}

/** 复用 Vue 团购商品详情接口。 */
export async function loadGroupPurchaseDetail(id: number | string): Promise<GroupPurchaseRow> {
    const response = await request.service.get<GroupPurchaseRow>(`/admin/product-group-purchase/vo/${id}`);
    return response.data;
}

/** 复用 Vue 团购商品审核接口。 */
export async function auditGroupPurchase(
    id: number | string,
    payload: { state: boolean; auditDescn: string }
): Promise<void> {
    await request.service.put(`/admin/product-group-purchase/audit/${id}`, payload);
}

/** 复用 Vue 团购商品下架接口。 */
export async function downShelfGroupPurchase(id: number | string): Promise<void> {
    await request.service.put(`/admin/product-group-purchase/down-shelf/${id}`);
}

/** 复用 Vue 团购商品关闭接口，并提交需要解除关联的文件 ID。 */
export async function closeGroupPurchase(id: number | string, unActiveFileIds: number[] = []): Promise<void> {
    await request.service.put(`/admin/product-group-purchase/close/${id}`, { unActiveFileIds });
}

/** 将 Vue 团购详情的团购、商品和发布状态枚举统一为数字。 */
function normalizeGroupPurchaseDetail(row: GroupPurchaseRow): GroupPurchaseRow {
    return {
        ...row,
        productGroupPurchase: {
            ...row.productGroupPurchase,
            groupPurchaseState:
                row.productGroupPurchase.groupPurchaseState == null
                    ? undefined
                    : Number(row.productGroupPurchase.groupPurchaseState),
            state: row.productGroupPurchase.state == null ? undefined : Number(row.productGroupPurchase.state),
            publishState:
                row.productGroupPurchase.publishState == null
                    ? undefined
                    : Number(row.productGroupPurchase.publishState)
        }
    };
}

const productStateLabels: Record<number, string> = { 1: "上架", 2: "下架" };
const publishStateLabels: Record<number, string> = {
    1: "待审核",
    2: "待提交",
    3: "已发布",
    4: "未通过",
    5: "已下架",
    6: "已关闭"
};
const groupStateLabels: Record<number, string> = { 1: "进行中", 2: "已完成", 3: "已结束" };
const waitingPaymentLabels: Record<number, string> = {
    1: "3小时",
    2: "12小时",
    3: "24小时",
    4: "48小时",
    5: "72小时",
    6: "5天",
    7: "7天",
    8: "不限时长"
};
const paymentTypeLabels: Record<number, string> = { 1: "线上支付", 2: "线下支付", 3: "线上支付、线下支付" };

/** 将旧 Vue 详情头的三段状态按原顺序组合。 */
function getGroupPurchaseStateDescription(product: GroupPurchaseRow["productGroupPurchase"]): string {
    return (
        [
            productStateLabels[product.state || 0],
            publishStateLabels[product.publishState || 0],
            groupStateLabels[product.groupPurchaseState || 0]
        ]
            .filter(Boolean)
            .join(" | ") || "-"
    );
}

/** 将旧 Vue 数值字段保持为带单位的详情文本。 */
function formatWithUnit(value: number | undefined, unit: string): string {
    return value == null ? "-" : `${value}${unit}`;
}

/** 保持旧 Vue 的提货周期展示，兼容接口只返回一端日期的情况。 */
function formatDeliveryPeriod(start?: string, end?: string): string {
    if (start && end) return `${start} 至 ${end}`;
    return start || end || "-";
}

/** 将旧 Vue 参数列表转换成 BaseItem 可直接渲染的文本。 */
function formatParameterInfo(parameterInfo?: GroupPurchaseRow["productGroupPurchase"]["parameterInfo"]): string {
    return parameterInfo?.length
        ? parameterInfo.map(item => `${item.parameterName || "-"} ${item.parameterValue || "-"}`).join("、")
        : "-";
}

/** 将旧 Vue 标签数组转换成详情文本。 */
function formatTags(tags?: string[]): string {
    return tags?.length ? tags.join("、") : "-";
}

/** 将旧 Vue 品类及产区简称保持为列表相同的展示格式。 */
function formatProductType(productType?: GroupPurchaseRow["productType"]): string {
    return productType?.name
        ? `${productType.name}${productType.areaAbbreviation ? ` (${productType.areaAbbreviation})` : ""}`
        : "-";
}

/** 团购商品详情页，展示 Vue 详情页中的交易、提货、结算和服务字段。 */
export function GroupPurchaseDetailPage({
    auditGroupPurchase: submitAudit = auditGroupPurchase,
    closeGroupPurchase: close = closeGroupPurchase,
    downShelfGroupPurchase: removeFromShelf = downShelfGroupPurchase,
    loadGroupPurchase = loadGroupPurchaseDetail,
    permissions
}: GroupPurchaseDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const id = new URLSearchParams(location.search).get("id");
    const isAuditPage = location.pathname.endsWith("/audit");
    const currentPermissions = permissions || storePermissions;
    const canDeal = currentPermissions.includes("ROLE_PRODUCT_DEAL");
    const canManage = currentPermissions.includes("ROLE_PRODUCT_MANAGE");
    const canClosePermission = currentPermissions.includes("ROLE_PRODUCT_CLOSE");
    const [detail, setDetail] = useState<GroupPurchaseRow>();
    const [product, setProduct] = useState<GroupPurchaseRow["productGroupPurchase"]>();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [auditState, setAuditState] = useState<boolean | null>(null);
    const [auditDescn, setAuditDescn] = useState("");
    const [auditSubmitting, setAuditSubmitting] = useState(false);

    useEffect(() => {
        if (!id) return;
        void loadGroupPurchase(id)
            .then(result => {
                const normalizedResult = normalizeGroupPurchaseDetail(result);
                setErrorMessage(null);
                setDetail(normalizedResult);
                setProduct(normalizedResult.productGroupPurchase);
            })
            .catch(() => setErrorMessage("团购商品详情加载失败"));
    }, [id, loadGroupPurchase]);

    const refreshDetail = async () => {
        if (!id) return;
        const normalizedResult = normalizeGroupPurchaseDetail(await loadGroupPurchase(id));
        setDetail(normalizedResult);
        setProduct(normalizedResult.productGroupPurchase);
    };

    const handleAudit = async () => {
        if (!id || auditState == null) return;
        if (!auditState && !auditDescn.trim()) {
            setErrorMessage("请填写驳回原因");
            return;
        }
        setAuditSubmitting(true);
        try {
            await submitAudit(id, { state: auditState, auditDescn: auditDescn.trim() });
            navigate("/productManagement/groupPurchase");
        } catch {
            setErrorMessage("团购商品审核失败");
        } finally {
            setAuditSubmitting(false);
        }
    };

    const handleClose = async () => {
        if (!id || !detail) return;
        const unActiveFileIds = [
            detail.mainFile?.id,
            detail.videoFile?.id,
            ...(detail.otherImgFile || []).map(file => file.id)
        ].filter((fileId): fileId is number => fileId != null);
        try {
            await close(id, unActiveFileIds);
            navigate("/productManagement/groupPurchase");
        } catch {
            // 关闭接口失败时保留当前详情，并反馈与 Vue 页面一致的失败状态。
            setErrorMessage("团购商品关闭失败");
        }
    };

    const handleDownShelf = async () => {
        if (!id) return;
        try {
            await removeFromShelf(id);
            await refreshDetail();
        } catch {
            // 下架或刷新接口失败时保留当前详情，并反馈与 Vue 页面一致的失败状态。
            setErrorMessage("团购商品下架失败");
        }
    };

    return (
        <section aria-label="group-purchase-detail-page">
            <Card>
                <ZhDetailHeader
                    leftInfos={[
                        { label: "商品编号", value: product?.id || "-" },
                        { label: "商品专员", value: product?.handlerName || "-" }
                    ]}
                    stateDesc={product ? getGroupPurchaseStateDescription(product) : "-"}
                    title={isAuditPage ? "审核团购商品" : "团购商品详情"}
                />
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {product ? (
                    <ZhMultiGrid>
                        <ZhGrid cols={4} gap={[30, 20]}>
                            <ZhGridItem span={4}>
                                {/* beta.13 BaseItem 会额外输出无障碍镜像，标题保留单一文本节点以匹配旧 Vue 查询语义。 */}
                                <div>商品标题: {product.title || "-"}</div>
                            </ZhGridItem>
                            <ZhBaseItem label="权重值" value={product.weight ?? "-"} />
                            <ZhBaseItem label="拼团单价" value={formatWithUnit(product.groupUnitPrice, "元/吨")} />
                            <ZhBaseItem
                                label="是否含税"
                                value={
                                    product.isIncludeTax === 1 ? "含税" : product.isIncludeTax === 0 ? "不含税" : "-"
                                }
                            />
                            <ZhBaseItem label="原价" value={formatWithUnit(product.unitPrice, "元/吨")} />
                            <ZhBaseItem label="拼团截止时间" value={product.groupEndTime || "-"} />
                            <ZhBaseItem
                                label="等待付款时间"
                                value={waitingPaymentLabels[product.waitingPayTime || 0] || "-"}
                            />
                            <ZhBaseItem label="拼团最低吨数" value={formatWithUnit(product.minOrderTon, "吨")} />
                            <ZhBaseItem
                                label="拼团成功吨数"
                                span={4}
                                value={formatWithUnit(product.groupSuccessTon, "吨")}
                            />
                            <ZhBaseItem
                                label="供应商"
                                value={
                                    product.supplierEnterprise?.enterpriseName ||
                                    product.supplierEnterprise?.supplierName ||
                                    "-"
                                }
                            />
                            <ZhBaseItem label="品类" value={formatProductType(detail?.productType)} />
                            <ZhBaseItem
                                label="品类标签"
                                value={formatTags(
                                    detail?.productTypeVo?.tags?.map(tag => tag.name).filter(Boolean) as string[]
                                )}
                            />
                            <ZhBaseItem label="参数值" span={4} value={formatParameterInfo(product.parameterInfo)} />
                            <ZhBaseItem label="商品标签" span={4} value={formatTags(product.productLabel)} />
                            <ProductDetailMapDialog location={product} />
                            <ZhBaseItem
                                label="提货周期"
                                value={formatDeliveryPeriod(product.deliveryStartTime, product.deliveryEndTime)}
                            />
                            <ZhBaseItem label="最小调整吨数" value={formatWithUnit(product.minAdjustmentTon, "吨")} />
                            <ZhBaseItem label="提货说明" span={4} value={product.deliveryDescn || "-"} />
                            <ZhBaseItem
                                label="下单要求"
                                value={
                                    product.settlementMethod === 1
                                        ? "全部货款"
                                        : product.settlementMethod === 2
                                          ? "支付定金"
                                          : "-"
                                }
                            />
                            <ZhBaseItem label="支付方式" value={paymentTypeLabels[product.payType || 0] || "-"} />
                            <ZhBaseItem label="规格" value={product.productTypeSpec || "-"} />
                            <ZhBaseItem label="商品介绍" span={4} value={product.introduce || "-"} />
                            <ZhBaseItem label="结算说明" span={4} value={product.settlementDescn || "-"} />
                            <ZhBaseItem label="服务保障" span={4} value={product.serviceGuarantee || "-"} />
                            <ZhBaseItem label="团购规则" span={4} value={product.regulation || "-"} />
                            <ZhBaseItem
                                label="商品专员联系电话"
                                span={3}
                                value={product.handlerMobile?.join("; ") || "-"}
                            />
                        </ZhGrid>
                    </ZhMultiGrid>
                ) : null}
                {isAuditPage && auditState != null ? (
                    <div className="mt-4">
                        <Input.TextArea
                            aria-label={auditState ? "审核意见" : "驳回原因"}
                            placeholder={auditState ? "请输入审核意见" : "请输入驳回原因"}
                            rows={4}
                            value={auditDescn}
                            onChange={event => setAuditDescn(event.target.value)}
                        />
                        <Button
                            aria-label="提交审核"
                            className="mt-3"
                            loading={auditSubmitting}
                            type="primary"
                            onClick={() => void handleAudit()}
                        >
                            提交审核
                        </Button>
                    </div>
                ) : null}
                <Space className="mt-4" wrap>
                    {isAuditPage ? (
                        <>
                            <Button aria-label="同意" onClick={() => setAuditState(true)}>
                                同意
                            </Button>
                            <Button aria-label="拒绝" onClick={() => setAuditState(false)}>
                                拒绝
                            </Button>
                        </>
                    ) : detail ? (
                        <>
                            {canManage && product?.publishState === 1 ? (
                                <Button
                                    aria-label="审核团购商品"
                                    onClick={() =>
                                        void navigate(`/productManagement/groupPurchase/audit?id=${id || ""}`)
                                    }
                                >
                                    审核
                                </Button>
                            ) : null}
                            {canDeal && product?.state === 2 && [1, 2, 4, 5].includes(product.publishState || 0) ? (
                                <Button
                                    aria-label="编辑团购商品"
                                    onClick={() =>
                                        void navigate(`/productManagement/groupPurchase/edit?id=${id || ""}`)
                                    }
                                >
                                    编辑
                                </Button>
                            ) : null}
                            {canClosePermission &&
                            product?.state === 2 &&
                            [1, 2, 4, 5].includes(product.publishState || 0) ? (
                                <Button aria-label="关闭团购商品" onClick={() => void handleClose()}>
                                    关闭
                                </Button>
                            ) : null}
                            {canDeal &&
                            product?.state === 1 &&
                            [3, 4].includes(product.publishState || 0) &&
                            product.groupPurchaseState === 1 ? (
                                <Button aria-label="下架团购商品" onClick={() => void handleDownShelf()}>
                                    下架
                                </Button>
                            ) : null}
                            {canDeal &&
                            product?.state === 2 &&
                            product.publishState === 6 &&
                            [2, 3].includes(product.groupPurchaseState || 0) ? (
                                <Button
                                    aria-label="复制团购商品"
                                    onClick={() =>
                                        void navigate(`/productManagement/groupPurchase/add?copyId=${id || ""}`)
                                    }
                                >
                                    复制
                                </Button>
                            ) : null}
                        </>
                    ) : null}
                    <Button onClick={() => void navigate("/productManagement/groupPurchase")}>返回</Button>
                </Space>
            </Card>
        </section>
    );
}

export default GroupPurchaseDetailPage;
